import { canCreateUsers } from '@/auth/roles';
import { useSession } from '@/auth/useSession';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { multiSelectOptions } from '@/components/ui/multi-select-utils';
import { useSchoolOptions } from '@/lib/api-call-hooks';
import { createCoachProfile, createUser, getUsers, updateUserRoles } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';
import { AllTimeZones, findTimeZoneByValue } from '@/lib/time-zones';
import { CreateUserCommonForm } from '@/routes/_layout/users/-components/create-user-common-form';
import {
  createUserCommonFormSchema,
  createUserOptionsFromFormData,
} from '@/routes/_layout/users/-components/form-schema';
import { coach, distinct, roleHash } from '@airreading/common';
import { UpdateCoachOptions, UserRoleAssignmentOptions, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

export interface CreateCoachProps {
  trigger?: ReactNode;
  fromUser?: UserWithRolesAndProfiles;
}

const formSchema = createUserCommonFormSchema.extend({
  isTrial: z.boolean().default(true),
  isSchoolAdmin: z.boolean().default(false),
  adminSchools: multiSelectOptions.default([]),
});
type Form = z.infer<typeof formSchema>;

export const CreateCoach = ({ trigger, fromUser }: CreateCoachProps) => {
  const { mutateAsync: createCoachProfileMutate } = useMutation({
    mutationFn: createCoachProfile,
  });

  const { mutateAsync: createUserMutate } = useMutation({
    mutationFn: createUser,
  });

  const { mutateAsync: updateUserRolesMutate } = useMutation({
    mutationFn: updateUserRoles,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const schoolOptions = useSchoolOptions();
  const { actingUser } = useSession();

  const onSubmit = ({ formData, closeModal }: SubmitProps<Form>) => {
    try {
      const { isTrial, isSchoolAdmin, adminSchools } = formData;

      if (isSchoolAdmin && !adminSchools.length) {
        toast({
          title: 'Please select a school to assign school admin role',
        });
        return;
      }

      const createUserPayload = createUserOptionsFromFormData(formData);
      const coachProfilePayload: UpdateCoachOptions = { isTrial };

      const assignRoles: UserRoleAssignmentOptions = [coach()];
      if (isSchoolAdmin) {
        assignRoles.push(
          ...adminSchools.map(({ value }) => ({
            role: 'school_admin' as const,
            schoolId: value,
            status: 'active' as const,
          })),
        );
      }

      const apiCall = fromUser
        ? async () => {
            await createCoachProfileMutate({ userId: fromUser.id, data: coachProfilePayload });
            await updateUserRolesMutate({
              userId: fromUser.id,
              roles: [
                ...fromUser.userRoles.map(({ role, schoolId, status }) => ({ role, schoolId, status })),
                ...assignRoles,
              ].filter(distinct(roleHash)),
            });
          }
        : () =>
            createUserMutate({
              ...createUserPayload,
              assignRoles,
              coachProfile: coachProfilePayload,
            });

      return apiCall()
        .then(() => {
          toast({
            title: 'Teacher created successfully',
          });
          closeModal();
          queryClient.invalidateQueries(getUsers());
          if (fromUser) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(fromUser.id) });
          }
        })
        .catch((error: unknown) => {
          const airServerError = airServerErrorFrom(error);
          const errorMessage = airServerError?.userFacingMessage || 'Teacher creation failed';

          toast({
            title: 'Teacher creation failed',
            description: errorMessage,
          });
        });
    } catch (error: unknown) {
      if (error instanceof TypeError) {
        toast({ title: error.message });
        return;
      }
    }
  };

  // With `fromUser` this only adds a coach profile and role to an existing
  // user, which is not user creation.
  if (!fromUser && !canCreateUsers(actingUser)) {
    return null;
  }

  return (
    <FormModal
      modalTitle="Create teacher"
      dialogContentProps={{ className: 'max-w-3xl' }}
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger || <Button>Create teacher</Button>}
      defaultValues={{
        isTrial: true,
        isSchoolAdmin: false,
        adminSchools: [],
        ...(fromUser
          ? {
              firstName: fromUser.firstName || undefined,
              lastName: fromUser.lastName || undefined,
              email: fromUser.email || undefined,
              phone: fromUser.phone || undefined,
              timeZone: findTimeZoneByValue(fromUser.timeZone) || AllTimeZones[0],
              edlinkId: undefined,
              iss: fromUser.externalKeys?.iss || undefined,
              sub: fromUser.externalKeys?.sub || undefined,
              isRegistrable: fromUser.isRegistrable,
            }
          : {}),
      }}
    >
      {({ form }) => {
        return (
          <div className="grid grid-cols-2 gap-2 py-4">
            <CreateUserCommonForm form={form} disabled={!!fromUser} />
            <FormCheckbox name="isTrial" label="Is trial" form={form} />
            <FormCheckbox name="isSchoolAdmin" label="Is School Admin" form={form} />
            <div className="col-span-2 space-y-2">
              {form.watch('isSchoolAdmin') && (
                <FormMultiSelect
                  form={form}
                  label="School"
                  name="adminSchools"
                  multipleSelectorProps={{
                    options: schoolOptions,
                    placeholder: 'Select schools the user is an admin for',
                  }}
                />
              )}
            </div>
          </div>
        );
      }}
    </FormModal>
  );
};
